import { Button } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { type CmsPagesExportScope, cms } from "../../../../api/cms.service";
import { downloadBlob } from "../../../../utils/csvExport";
import { PAGE_SECTIONS, type PageSection, isPageSection } from "../../pageSection";
import styles from "./PageExportSection.module.css";

export type ExportSidebarState =
  | { status: "none" }
  | { status: "loading" }
  | { status: "failed" }
  | { status: "ready"; pageCount: number };

type PageExportSectionProps = {
  pageId: string;
  sidebar: ExportSidebarState;
};

function pagesLabel(count: number): string {
  return count === 1 ? "1 page" : `${count} pages`;
}

export function PageExportSection({ pageId, sidebar }: PageExportSectionProps) {
  const [exportScope, setExportScope] = useState<CmsPagesExportScope>("page");
  const [exportSection, setExportSection] = useState<PageSection | "">("");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const sidebarReady = sidebar.status === "ready";
  const sidebarLoading = sidebar.status === "loading";
  const showScopeChoice = sidebar.status !== "none";

  async function downloadExport() {
    setExporting(true);
    setExportError(null);
    try {
      const scope = sidebarReady ? exportScope : "page";
      const file = await cms.exportPages(
        pageId,
        scope,
        scope === "sidebar" && exportSection !== "" ? exportSection : undefined,
      );
      downloadBlob(file.blob, file.filename);
    } catch (err) {
      const reason = err instanceof Error ? ` ${err.message}` : "";
      setExportError(`The file couldn't be downloaded.${reason}`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <section className={styles.exportSection} aria-labelledby="page-export-title">
      <h2 id="page-export-title" className={styles.title}>
        Export
      </h2>

      <p className={styles.hint}>
        Use the file to copy pages to another environment, e.g. from dev to live.
      </p>

      {showScopeChoice && (
        <fieldset className={styles.scopeChoice}>
          <legend className={styles.legend}>What to export</legend>

          <label className={styles.scopeOption}>
            <input
              type="radio"
              name="page-export-scope"
              value="page"
              checked={exportScope === "page"}
              onChange={() => setExportScope("page")}
            />
            This page
          </label>

          <label className={styles.scopeOption}>
            <input
              type="radio"
              name="page-export-scope"
              value="sidebar"
              checked={sidebarReady && exportScope === "sidebar"}
              onChange={() => setExportScope("sidebar")}
              disabled={!sidebarReady}
            />
            This page and its sidebar
            {sidebar.status === "ready" && ` (${pagesLabel(sidebar.pageCount)})`}
            {sidebarLoading && " (Loading…)"}
          </label>
        </fieldset>
      )}

      {sidebarReady && exportScope === "sidebar" && (
        <div className={styles.sectionFilter}>
          <label className={styles.legend} htmlFor="page-export-section">
            Pages from
          </label>
          <select
            id="page-export-section"
            className={styles.select}
            value={exportSection}
            onChange={event =>
              setExportSection(isPageSection(event.target.value) ? event.target.value : "")
            }
          >
            <option value="">All sections</option>
            {PAGE_SECTIONS.map(section => (
              <option key={section.value} value={section.value}>
                {section.label} only
              </option>
            ))}
          </select>
        </div>
      )}

      {sidebar.status === "failed" && (
        <p className={styles.error} role="alert">
          Sidebar details couldn't be loaded. Reload the page to try again.
        </p>
      )}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="outlined"
          color="primary"
          onClick={downloadExport}
          disabled={exporting || sidebarLoading}
        >
          {exporting ? "Preparing file…" : "Download file"}
        </Button>
      </div>

      {exportError && (
        <p className={styles.error} role="alert">
          {exportError}
        </p>
      )}
    </section>
  );
}
